import { useEffect, useRef, useState } from 'react';
import Icon3D from '../Icons3D';
import { useCopilot } from '../../api/copilot';
import { callSafely } from './util';

const BLOCKED_TEXT = "Microphone blocked — allow it in the browser's site settings";
/* Long enough to read the sentence twice; it can be dismissed sooner. */
const POPOVER_MS = 7000;

/**
 * Tap to talk, tap again to stop.
 *
 * Disabled until an agent is in the room: a microphone that opens onto nobody
 * turns speech into silence with no explanation. The one exception is a mic
 * that is already on — whoever is speaking must always be able to switch it
 * off, connected or not.
 *
 * `paused` is the composer's "you are typing" state. The provider keeps the mic
 * open (the intent survives), but showing it as listening while someone types
 * reads as the app transcribing their keyboard, so the display rests until the
 * typing stops.
 *
 * A blocked microphone cannot be fixed from the page — the permission lives in
 * the browser's own site settings — so the button says where to go, in a small
 * popover that opens when the browser refuses and on every press while it is
 * still refused.
 */
export default function MicButton({ paused = false, size = 19 }) {
  const {
    mic = 'off', available = false, toggleMic, unlockAudio,
  } = useCopilot();
  const [pop, setPop] = useState(false);
  const wrapRef = useRef(null);
  const prevMic = useRef(mic);

  /* Open the explanation when a request ends in a refusal — the moment the
     reader is looking at the button waiting for it to light up. */
  useEffect(() => {
    if (mic === 'blocked' && prevMic.current !== 'blocked') setPop(true);
    if (mic !== 'blocked') setPop(false);
    prevMic.current = mic;
  }, [mic]);

  /* Dismissed by time, by Escape or by a press anywhere else. Listeners exist
     only while it is open. */
  useEffect(() => {
    if (!pop) return undefined;
    const timer = setTimeout(() => setPop(false), POPOVER_MS);
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setPop(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setPop(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [pop]);

  const on = mic === 'on';
  const showListening = on && !paused;
  const disabled = !available && !on;

  let title;
  if (disabled) title = 'Voice is available once the assistant is connected';
  else if (mic === 'requesting') title = 'Waiting for microphone permission…';
  else if (mic === 'blocked') title = BLOCKED_TEXT;
  else if (mic === 'unavailable') title = 'No microphone found — plug one in and press to try again';
  else if (on && paused) title = 'Listening paused while you type — press to turn the mic off';
  else if (on) title = 'Listening — press to stop';
  else title = 'Talk to the assistant';
  /* The accessible name stays put across on and off — `aria-pressed` carries
     that — and only names the states a press cannot fix by itself. */
  let label = 'Talk to the assistant';
  if (mic === 'blocked') label += ' — microphone blocked';
  else if (mic === 'unavailable') label += ' — no microphone found';

  const press = () => {
    if (mic === 'requesting') return;
    /* Turning the mic on is a user gesture, which is what the browser needs
       before it will play the spoken reply that follows. */
    if (!on) callSafely(unlockAudio);
    if (mic === 'blocked') setPop(true);
    callSafely(toggleMic);
  };

  const cls = ['mic', 'cp-mic', showListening && 'listening', on && paused && 'paused', mic === 'requesting' && 'requesting', (mic === 'blocked' || mic === 'unavailable') && 'blocked']
    .filter(Boolean)
    .join(' ');

  return (
    <span className="cp-mic-wrap" ref={wrapRef}>
      <button
        type="button"
        className={cls}
        title={title}
        aria-label={label}
        aria-pressed={on}
        aria-busy={mic === 'requesting' || undefined}
        disabled={disabled}
        onClick={press}
      >
        <Icon3D name="mic" size={size} />
      </button>
      {pop && (
        <span className="cp-pop" role="alert">
          {BLOCKED_TEXT}
        </span>
      )}
    </span>
  );
}
